import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#EDECEA]" data-testid="privacy-page">
      <Navigation />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-3xl mx-auto px-6 md:px-12 pt-32 pb-20"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-[#9B9589] hover:text-[#0F1B2D] transition-colors mb-10"
          data-testid="privacy-back-link"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <div className="h-[2px] w-10 bg-[#C4A35A]" />
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#9B9589] font-bold">
            Legal
          </p>
        </div>

        <h1 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-semibold text-[#0F1B2D] tracking-tight mb-3">
          Privacy Policy
        </h1>
        <p className="text-sm font-medium text-[#9B9589] mb-12">
          Last updated: January 2025 &middot; Effective immediately upon publication
        </p>

        <div className="space-y-10">
          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              1. Introduction
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              The Artist Identification Network & Accreditation Archive ("AINAA", "we", "our", or "us") is committed to protecting the privacy and personal data of all applicants, members, and visitors to our platform. This Privacy Policy describes the types of information we collect, how we use and safeguard that information, and the rights available to you as a data subject. By using our services or submitting an application, you acknowledge and accept the terms of this policy.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              2. Information We Collect
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
              We collect and process the following categories of personal data:
            </p>
            <div className="space-y-3 ml-4">
              <div>
                <p className="text-sm font-semibold text-[#0F1B2D]">a. Identity & Contact Data</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">Full legal name, email address, PAN (Permanent Account Number), and professional title as submitted during the membership application process.</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0F1B2D]">b. Professional Data</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">Artistic discipline, primary field of practice, project completion records, peer review submissions, and professional history used for Trust Score calculation.</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0F1B2D]">c. Behavioural Data</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">Communication response times, deadline adherence patterns, dispute records, and growth trajectory data processed by the Introwspect&trade; algorithm.</p>
              </div>
              <div>
                <p className="text-sm font-semibold text-[#0F1B2D]">d. Technical Data</p>
                <p className="text-sm text-[#4A4640] font-medium leading-relaxed">Browser type, IP address, device information, and session data collected automatically when you access our platform.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              3. How We Use Your Information
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
              Your personal data is used exclusively for the following purposes:
            </p>
            <ul className="list-disc list-inside text-sm text-[#4A4640] font-medium space-y-2 ml-4">
              <li>Processing and evaluating membership applications</li>
              <li>Calculating and maintaining your Trust Score across the five dimensions (Credibility, Communication, Discipline, Consistency, Growth)</li>
              <li>Generating personalised behavioural insights through Introwspect&trade;</li>
              <li>Providing AI-assisted recommendations and support through Specter&trade;</li>
              <li>Facilitating legal aid and financial support services for eligible members</li>
              <li>Communicating official notices, circulars, and service updates</li>
              <li>Verifying identity and preventing fraudulent applications</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              4. Trust Score & Behavioural Data
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              Your Trust Score is calculated from verified professional data — not self-reported information or social media activity. The dimensional breakdown, underlying data, and behavioural insights generated by Introwspect&trade; are visible only to you. Other members may view your overall Trust Score and verification status, but not the individual dimension scores or source data. AINAA does not sell, license, or share Trust Score data with third parties without your explicit written consent.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              5. Data Sharing & Disclosure
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
              We do not sell your personal data. We may share information only in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-sm text-[#4A4640] font-medium space-y-2 ml-4">
              <li>With the AINAA Legal Aid Panel, when you initiate a legal support request</li>
              <li>With institutional partners (galleries, festivals) who request your Trust Score — only with your prior consent</li>
              <li>With regulatory or law enforcement authorities, when required by applicable law</li>
              <li>With service providers who assist in operating our platform, under strict data processing agreements</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              6. Data Security
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              AINAA implements industry-standard technical and organisational security measures. All personal data is encrypted at rest and in transit. Access to Trust Score calculation systems and behavioural data is restricted to authorised personnel only. We conduct regular security audits and maintain incident response procedures in accordance with applicable data protection regulations.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              7. Data Retention
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              We retain your personal data for the duration of your membership. Waitlist applicant data is retained for a maximum of 24 months. Upon membership termination or withdrawal of your application, your personal data will be deleted within 90 days, except where retention is required by law or for the resolution of ongoing disputes.
            </p>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              8. Your Rights
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed mb-4">
              Subject to applicable law, you have the right to:
            </p>
            <ul className="list-disc list-inside text-sm text-[#4A4640] font-medium space-y-2 ml-4">
              <li>Access the personal data we hold about you</li>
              <li>Correct inaccurate or incomplete data</li>
              <li>Request deletion of your data ("right to be forgotten")</li>
              <li>Export your data in a portable format</li>
              <li>Withdraw consent for data processing at any time</li>
              <li>Lodge a complaint with the relevant data protection authority</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Playfair_Display'] text-xl font-semibold text-[#0F1B2D] mb-4">
              9. Contact
            </h2>
            <p className="text-sm text-[#4A4640] font-medium leading-relaxed">
              For questions, requests, or concerns regarding this Privacy Policy or the processing of your personal data, contact us at{' '}
              <a href="mailto:privacy@ainaa.co" className="text-[#0F1B2D] font-semibold hover:text-[#C4A35A] transition-colors underline">
                privacy@ainaa.co
              </a>
            </p>
          </section>
        </div>
      </motion.div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
